import { useState, useCallback, useEffect, useMemo } from 'react';

export function useAreasFilter({
  config,
  query,
  setFilters,
  updateQuery,
}) {
  let { defaultCountry, towns, suburbs } = useConfigParams(config);
  let { areas, onAreaSelect, onAreaRemove } = useAreas({ towns, suburbs, query, setFilters, updateQuery });
  let { areaQuery, onAreaQueryChange } = useAreaQuery();
  let areaMatches = useAreaMatches({ suburbs, towns, defaultCountry, areaQuery });

  return {
    areas,
    areaQuery,
    areaMatches,
    onAreaQueryChange,
    onAreaSelect,
    onAreaRemove,
  };
}

function useConfigParams(config) {
  let defaultCountry = useMemo(() => config.defaultCountry?.toLowerCase(), []);

  let towns = useMemo(() => config.towns.map(x => ({
    town: x.town.toLowerCase(),
    province: x.province.toLowerCase(),
    country: x.country?.toLowerCase(),
    id: `${x.province}+${x.town}`,
    term: x.town,
    subterm: defaultCountry !== x.country.toLowerCase() ? `, ${x.country}` : undefined,
    type: `town`,
    value: { town: x.town },
  })), []);

  let suburbs = useMemo(() => config.suburbs.map(x => ({
    suburb: x.suburb.toLowerCase(),
    town: x.town.toLowerCase(),
    province: x.province.toLowerCase(),
    country: x.country?.toLowerCase(),
    id: `${x.province}+${x.town}+${x.suburb}`,
    term: x.suburb,
    type: `suburb`,
    value: { town: x.town, suburb: x.suburb },
  })), []);

  return { defaultCountry, towns, suburbs };
}

function useAreas({ towns, suburbs, query, setFilters, updateQuery }) {
  let defaultAreas = useMemo(() => {
    let defaultAreas = [];

    for (let area of query.areas || []) {
      if (area.suburb) {
        let suburb = suburbs.find(x => x.suburb === area.suburb.toLowerCase());
        if (suburb) defaultAreas.push(suburb);
      } else {
        let town = towns.find(x => x.town === area.town.toLowerCase());
        if (town) defaultAreas.push(town);
      }
    }

    return defaultAreas;
  }, []);

  let [areas, setAreas] = useState(defaultAreas);

  let onAreaSelect = useCallback((area) => {
    setAreas(areas => {
      if (areas.some(x =>
        x.id === area.id
        || (area.type === `suburb` && x.type === `town` && area.town === x.town))) {
          return areas;
      }

      if (area.type === `town`) areas = areas.filter(x => x.town !== area.town);

      return [...areas, area];
    });
  }, []);

  let onAreaRemove = useCallback((area) => {
    setAreas(x => x.filter(x => x.id !== area.id));
  }, []);

  useEffect(() => {
    let newValue = areas.map(x => x.value);
    setFilters(x => ({ ...x, areas: newValue }));
    updateQuery({ areas: newValue.length ? newValue : undefined });
  }, [areas]);

  return { areas, onAreaSelect, onAreaRemove };
}

function useAreaQuery() {
  let [areaQuery, setAreaQuery] = useState('');
  let onAreaQueryChange = useCallback((value) => setAreaQuery(value), []);

  return { areaQuery, onAreaQueryChange };
}

function useAreaMatches({ suburbs, towns, defaultCountry, areaQuery }) {
  return useMemo(() => {
    if (areaQuery.length === 0) return [...towns, ...suburbs].sort(sortBySuburbThenByTown);

    let matches = [];
    let matchedTowns = new Set();

    areaQuery = areaQuery.toLowerCase();

    for (let suburbRecord of suburbs) {
      let { suburb, town, country } = suburbRecord;

      if (
        suburb.includes(areaQuery)
        || town.includes(areaQuery)
        || (country && country !== defaultCountry && country.includes(areaQuery))
      ) {
        if (!matchedTowns.has(town)) {
          matchedTowns.add(town);
          matches.push(towns.find(x => x.town === town));
        }

        matches.push(suburbRecord);
      }
    }

    matches.sort(sortBySuburbThenByTown);

    return matches;
  }, [areaQuery]);
}

function sortBySuburbThenByTown(a,b) {
  if (a.town > b.town) return 1;
  if (a.town < b.town) return -1;

  if (a.suburb === undefined) return -1;
  if (b.suburb === undefined) return 1;

  if (a.suburb > b.suburb) return 1;
  if (a.suburb < b.suburb) return -1;

  return 0;
}
